import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { UserPlus, Unlink, RefreshCw, ShieldAlert, Users2, Plus, Link as LinkIcon } from "lucide-react";

/**
 * /admin/team-owners — Team ↔ Owner relationship management.
 * Super Admin / League Admin can:
 *   • See which teams have a linked owner login
 *   • Assign an EXISTING user to a team
 *   • Create a fresh team + owner in one action
 *   • Unassign / reassign owners
 *   • Spot orphan / unassigned owner accounts
 */
export default function TeamOwnersAdmin() {
  const [matrix, setMatrix] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [assignFor, setAssignFor] = useState(null); // team row being edited
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [m, u] = await Promise.all([
        api.get("/admin/team-owners-matrix"),
        api.get("/users"),
      ]);
      setMatrix(m.data);
      setAllUsers(u.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const unassign = async (teamId, userId) => {
    if (!window.confirm("Detach this owner from the team? Their role drops to spectator.")) return;
    setBusy(true);
    try {
      await api.post(`/teams/${teamId}/unassign-owner/${userId}`);
      toast.success("Owner detached");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (!matrix) {
    return <div className="p-8 text-zinc-500" data-testid="team-owners-loading">Loading team-owner map…</div>;
  }

  const { teams, unassigned_owners = [], orphan_owners = [] } = matrix;

  return (
    <div className="p-6 md:p-10 max-w-7xl" data-testid="team-owners-admin">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#F4B400]">Access Control</div>
          <h1 className="font-anton text-5xl md:text-6xl uppercase tracking-tighter text-white leading-none">
            Team Owner Management
          </h1>
          <div className="text-xs text-zinc-400 mt-2">
            Every Team Owner login must be explicitly linked to a Team. Connections
            below are enforced at the database + API level.
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setCreateOpen(true)}
            className="bg-[#F4B400] text-black font-bold uppercase tracking-widest"
            data-testid="btn-create-team-owner"
          >
            <Plus className="w-4 h-4 mr-2" /> Create Team + Owner
          </Button>
          <Button
            variant="outline"
            onClick={load}
            className="border-white/20 text-white"
            data-testid="btn-reload-matrix"
          >
            <RefreshCw className="w-4 h-4 mr-2" /> Reload
          </Button>
        </div>
      </div>

      {/* Summary chips */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        <StatChip label="Total Teams" value={matrix.total_teams} tone="neutral" testid="stat-teams-total" />
        <StatChip label="Connected" value={matrix.connected_teams} tone="ok" testid="stat-teams-connected" />
        <StatChip label="Missing Owner" value={matrix.unconnected_teams} tone="warn" testid="stat-teams-unconnected" />
        <StatChip label="Unassigned Owner Logins" value={unassigned_owners.length} tone="warn" testid="stat-owners-unassigned" />
      </div>

      {/* Orphan warnings */}
      {orphan_owners.length > 0 && (
        <Card className="p-4 bg-red-950/40 border-red-800/50 mb-6" data-testid="orphan-owner-warning">
          <div className="flex items-start gap-3">
            <ShieldAlert className="w-5 h-5 text-red-400 mt-0.5" />
            <div>
              <div className="font-bold text-red-200">Broken links detected</div>
              <div className="text-xs text-red-300/80 mt-1">
                {orphan_owners.length} owner login(s) point at a deleted/missing team_id.
                Reassign or clean these up:
              </div>
              <ul className="text-xs text-red-200 mt-2 space-y-1">
                {orphan_owners.map(o => (
                  <li key={o.id} className="flex items-center gap-2" data-testid={`orphan-${o.id}`}>
                    • {o.name} · <span className="opacity-70">{o.email}</span>
                    <Button
                      size="sm"
                      variant="outline"
                      className="ml-2 h-6 border-red-700 text-red-200"
                      onClick={() => setAssignFor({ team_id: null, name: null, forceUser: o })}
                      data-testid={`orphan-reassign-${o.id}`}
                    >
                      Reassign
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Card>
      )}

      {/* Team ↔ Owner matrix */}
      <Card className="bg-[#0d1a13] border-white/10 overflow-hidden mb-8">
        <div className="p-4 border-b border-white/10 flex items-center gap-2">
          <Users2 className="w-5 h-5 text-[#F4B400]" />
          <div className="font-anton text-xl uppercase text-white">Team Owner Matrix</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] uppercase tracking-widest text-zinc-500 border-b border-white/5">
                <th className="text-left px-4 py-3">Team</th>
                <th className="text-left px-4 py-3">Group</th>
                <th className="text-left px-4 py-3">Linked Owner(s)</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {teams.map(row => (
                <tr key={row.team_id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`team-row-${row.team_id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {row.logo_url ? (
                        <img src={row.logo_url} alt={row.team_name} className="w-9 h-9 rounded object-contain bg-black/40" />
                      ) : (
                        <div className="w-9 h-9 rounded bg-black/40 border border-white/10 flex items-center justify-center text-[10px] text-zinc-500">?</div>
                      )}
                      <div>
                        <div className="text-white font-semibold">{row.team_name}</div>
                        <div className="text-[10px] text-zinc-500">{row.slug || row.team_id.slice(0, 8)}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {row.group ? <Badge className="bg-[#F4B400]/20 text-[#F4B400] border-[#F4B400]/40">Group {row.group}</Badge> : <span className="text-zinc-600">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    {row.owners.length === 0 ? (
                      <span className="text-zinc-500 italic text-xs">no owner assigned</span>
                    ) : (
                      <div className="space-y-1">
                        {row.owners.map(o => (
                          <div key={o.id} className="flex items-center gap-2 flex-wrap" data-testid={`team-owner-${row.team_id}-${o.id}`}>
                            <span className="text-white text-sm">{o.name}</span>
                            <span className="text-[11px] text-zinc-500">{o.email}</span>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-6 px-2 border-red-800/50 text-red-300 hover:bg-red-900/30"
                              onClick={() => unassign(row.team_id, o.id)}
                              disabled={busy}
                              data-testid={`btn-unassign-${row.team_id}-${o.id}`}
                            >
                              <Unlink className="w-3 h-3 mr-1" /> Detach
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {row.status === "connected"
                      ? <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30">Connected</Badge>
                      : <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30">Not connected</Badge>
                    }
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      size="sm"
                      className="bg-[#F4B400] text-black font-bold"
                      onClick={() => setAssignFor(row)}
                      data-testid={`btn-assign-${row.team_id}`}
                    >
                      <LinkIcon className="w-3.5 h-3.5 mr-1.5" /> Assign
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Unassigned owner logins */}
      <Card className="p-4 bg-[#0d1a13] border-white/10" data-testid="unassigned-owners-card">
        <div className="flex items-center gap-2 mb-3">
          <UserPlus className="w-5 h-5 text-[#F4B400]" />
          <div className="font-anton text-xl uppercase text-white">Unassigned Owner Logins</div>
        </div>
        {unassigned_owners.length === 0 ? (
          <div className="text-xs text-zinc-500">Every owner account is linked to a team. Nice.</div>
        ) : (
          <ul className="text-sm space-y-2">
            {unassigned_owners.map(o => (
              <li key={o.id} className="flex items-center justify-between gap-2 py-1 border-b border-white/5" data-testid={`unassigned-${o.id}`}>
                <div>
                  <div className="text-white">{o.name}</div>
                  <div className="text-[11px] text-zinc-500">{o.email}</div>
                </div>
                <Button
                  size="sm"
                  className="bg-[#F4B400] text-black font-bold"
                  onClick={() => setAssignFor({ team_id: null, name: null, forceUser: o })}
                  data-testid={`btn-assign-existing-${o.id}`}
                >
                  Link to team
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* Dialogs */}
      <AssignDialog
        open={!!assignFor}
        row={assignFor}
        allUsers={allUsers}
        teams={teams}
        onClose={() => setAssignFor(null)}
        onDone={async () => { setAssignFor(null); await load(); }}
      />
      <CreateTeamOwnerDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onDone={async () => { setCreateOpen(false); await load(); }}
      />
    </div>
  );
}

function StatChip({ label, value, tone, testid }) {
  const cl = tone === "ok"
    ? "border-emerald-500/40 text-emerald-300"
    : tone === "warn"
    ? "border-amber-500/40 text-amber-300"
    : "border-white/10 text-zinc-300";
  return (
    <div className={`px-4 py-3 rounded-xl bg-[#0d1a13] border ${cl}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest opacity-80">{label}</div>
      <div className="text-3xl font-anton mt-1">{value}</div>
    </div>
  );
}

function AssignDialog({ open, row, allUsers, teams, onClose, onDone }) {
  const [userId, setUserId] = useState("");
  const [teamId, setTeamId] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setUserId(row?.forceUser?.id || "");
      setTeamId(row?.team_id || "");
    }
  }, [open, row]);

  const candidateUsers = useMemo(() => {
    // Show users that are owners OR spectators (can be promoted). Skip
    // super_admin / league_admin — they shouldn't be tied to a single team.
    return (allUsers || []).filter(u => ["owner", "spectator"].includes(u.role));
  }, [allUsers]);

  const submit = async () => {
    if (!teamId) return toast.error("Pick a team");
    if (!userId) return toast.error("Pick a user account");
    setBusy(true);
    try {
      await api.post(`/teams/${teamId}/assign-owner`, { user_id: userId });
      toast.success("Owner linked to team");
      onDone?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="bg-[#0f1a15] border-white/10 text-white" data-testid="assign-owner-dialog">
        <DialogHeader>
          <DialogTitle>Assign Team Owner</DialogTitle>
          <DialogDescription className="text-zinc-400">
            Bind an existing user account to a team. The user&apos;s role is set to
            <b> owner</b> and <b>team_id</b> is written to the users record.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-zinc-400 mb-1">Team</div>
            <Select value={teamId} onValueChange={setTeamId} disabled={!!row?.team_id}>
              <SelectTrigger data-testid="assign-team-select">
                <SelectValue placeholder="— pick a team —" />
              </SelectTrigger>
              <SelectContent>
                {teams.map(t => (
                  <SelectItem key={t.team_id} value={t.team_id}>{t.team_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-zinc-400 mb-1">User account</div>
            <Select value={userId} onValueChange={setUserId}>
              <SelectTrigger data-testid="assign-user-select">
                <SelectValue placeholder="— pick a user —" />
              </SelectTrigger>
              <SelectContent>
                {candidateUsers.map(u => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.name} · {u.email} {u.team_id ? "(linked)" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="text-[10px] text-zinc-500 mt-1">
              Only users with role=owner or spectator are shown. Promoted spectators become team owners.
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="assign-cancel">Cancel</Button>
          <Button className="bg-[#F4B400] text-black font-bold" onClick={submit} disabled={busy} data-testid="assign-confirm">
            {busy ? "Linking…" : "Assign"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateTeamOwnerDialog({ open, onClose, onDone }) {
  const empty = { name: "", slug: "", owner_name: "", owner_email: "", owner_password: "" };
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) setForm(empty); }, [open]);

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Team name is required");
    if (!form.owner_name.trim()) return toast.error("Owner name is required");
    if (!form.owner_email.trim()) return toast.error("Owner email is required");
    if (!form.owner_password || form.owner_password.length < 6) return toast.error("Owner password must be ≥ 6 chars");
    setBusy(true);
    try {
      const payload = { ...form, slug: form.slug.trim() || null };
      const { data } = await api.post("/teams/create-with-owner", payload);
      toast.success(`Created ${data.team.name} + owner ${data.owner.email}`);
      onDone?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="bg-[#0f1a15] border-white/10 text-white max-w-lg" data-testid="create-team-owner-dialog">
        <DialogHeader>
          <DialogTitle>Create Team + Owner Login</DialogTitle>
          <DialogDescription className="text-zinc-400">
            One atomic action: creates the Team, creates the Owner user, and
            writes the relationship — so the owner sees their team immediately on login.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Field label="Team name" value={form.name} onChange={v => setForm({...form, name: v})} testid="ct-team-name" />
          <Field label="Team slug (optional)" value={form.slug} onChange={v => setForm({...form, slug: v})} testid="ct-team-slug" placeholder="lowercase-hyphens" />
          <div className="pt-2 border-t border-white/10" />
          <Field label="Owner name" value={form.owner_name} onChange={v => setForm({...form, owner_name: v})} testid="ct-owner-name" />
          <Field label="Owner email" value={form.owner_email} onChange={v => setForm({...form, owner_email: v})} testid="ct-owner-email" placeholder="owner@example.com" />
          <Field label="Owner password" value={form.owner_password} onChange={v => setForm({...form, owner_password: v})} type="password" testid="ct-owner-password" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="ct-cancel">Cancel</Button>
          <Button className="bg-[#F4B400] text-black font-bold" onClick={submit} disabled={busy} data-testid="ct-submit">
            {busy ? "Creating…" : "Create + Link"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, testid }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-zinc-400 mb-1">{label}</div>
      <Input value={value} onChange={e => onChange(e.target.value)} type={type} placeholder={placeholder} data-testid={testid} />
    </div>
  );
}
